import React, { useEffect, useState } from "react";

/**
 * Typewriter-style terminal that reveals lines one by one.
 * Accepts an array of { type, text, delay?, hint? } lines.
 */
const Terminal = ({ title = "vedant@dev — zsh", lines = [], className = "" }) => {
  const [visible, setVisible] = useState([]);
  const [currentChars, setCurrentChars] = useState("");
  const [currentIdx, setCurrentIdx] = useState(0);

  // Reveal lines sequentially
  useEffect(() => {
    if (currentIdx >= lines.length) return undefined;
    const line = lines[currentIdx];
    const delay = line.delay ?? 120;
    const startTimer = setTimeout(() => {
      let i = 0;
      const text = line.text || "";
      const speed = line.type === "prompt" ? 18 : line.type === "json" ? 8 : 10;
      const typeInterval = setInterval(() => {
        i += 1;
        setCurrentChars(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(typeInterval);
          setVisible((v) => [...v, line]);
          setCurrentChars("");
          setCurrentIdx((idx) => idx + 1);
        }
      }, speed);
    }, delay);
    return () => {
      clearTimeout(startTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, lines.length]);

  const renderLine = (line, isCurrent = false, text) => {
    const display = isCurrent ? text : line.text;
    if (line.type === "prompt") {
      return (
        <div className="flex gap-2">
          <span className="text-emerald-400 shrink-0">❯</span>
          <span className={line.hint ? "text-amber-200/80" : "text-zinc-100"}>
            {display}
            {isCurrent && (
              <span className="inline-block w-1.5 h-3.5 bg-cyan-300 align-middle ml-0.5 animate-pulse" />
            )}
          </span>
        </div>
      );
    }
    if (line.type === "json") {
      return (
        <div className="pl-4 text-cyan-200/90">
          <span>{display}</span>
          {isCurrent && (
            <span className="inline-block w-1.5 h-3.5 bg-cyan-300 align-middle ml-0.5 animate-pulse" />
          )}
        </div>
      );
    }
    // output
    return (
      <div className="pl-4 text-zinc-400">
        <span>{display}</span>
        {isCurrent && (
          <span className="inline-block w-1.5 h-3.5 bg-cyan-300 align-middle ml-0.5 animate-pulse" />
        )}
      </div>
    );
  };

  return (
    <div className={`rounded-2xl overflow-hidden glass-strong ${className}`}>
      {/* chrome */}
      <div className="flex items-center gap-2 h-9 px-4 border-b border-white/5 bg-[#0a0b10]">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
        <span className="flex-1 text-center font-mono text-[11px] text-zinc-500 tracking-wider truncate px-2">
          {title}
        </span>
        <span className="font-mono text-[10px] text-emerald-300/70">●</span>
      </div>
      {/* body */}
      <div className="p-5 font-mono text-[12.5px] leading-relaxed space-y-1.5 min-h-[280px]">
        {visible.map((l, i) => (
          <div key={i}>{renderLine(l)}</div>
        ))}
        {currentIdx < lines.length &&
          currentChars !== undefined &&
          renderLine(lines[currentIdx], true, currentChars)}
        {currentIdx >= lines.length && (
          <div className="flex gap-2 pt-1">
            <span className="text-emerald-400">❯</span>
            <span className="inline-block w-1.5 h-3.5 bg-cyan-300 align-middle mt-1 animate-pulse" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Terminal;
